/**
 * Lightweight, SSR-safe network quality detector.
 * Defaults to 'fast' when the Network Information API is unavailable
 * (Safari, Firefox desktop, etc.) — we never assume "slow" without evidence.
 *
 * Side-effect on import: sets `<html data-net="...">` so CSS can target
 * Save-Data / 2G branches (see index.css). Listens for connection changes
 * and updates the attribute in place — no React re-render involved.
 */

export type NetSpeed = 'fast' | 'slow' | 'unknown';

const getConnection = (): any => {
  if (typeof navigator === 'undefined') return null;
  const n = navigator as any;
  return n.connection || n.mozConnection || n.webkitConnection || null;
};

export const detectNetSpeed = (): NetSpeed => {
  const conn = getConnection();
  if (!conn) return 'unknown';
  if (conn.saveData === true) return 'slow';
  const t = conn.effectiveType as string | undefined;
  if (t === 'slow-2g' || t === '2g') return 'slow';
  if (t === '3g' || t === '4g') return 'fast';
  return 'unknown';
};

export const isSlowNetwork = () => detectNetSpeed() === 'slow';

/** Apply a `data-net` attribute to <html> and keep it in sync. */
export const initNetworkQuality = () => {
  if (typeof document === 'undefined') return;
  const apply = () => {
    const speed = detectNetSpeed();
    document.documentElement.setAttribute('data-net', speed);
  };
  apply();
  const conn = getConnection();
  if (conn && typeof conn.addEventListener === 'function') {
    try {
      conn.addEventListener('change', apply);
    } catch {
      /* noop */
    }
  }
};
